import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import styled, { css } from "styled-components";

import {
  AnnotationIcon,
  CloseIcon,
  EpisodeIcon,
  FrameIcon,
  PauseIcon,
  PlayIcon,
  RotateCcwIcon,
} from "../../icons/glyphs";
import { isRenderableNode } from "../../utils/reactNode";
import { Filter, FilterController, type FilterGroup, type FilterSelection } from "../Filter";
import { IconButton } from "../IconButton";
import { LabelChip } from "../LabelChip";
import { ListView, type ListViewItemContext, type ListViewProps } from "../ListView";
import { NotificationBadge } from "../NotificationBadge";
import { Scrollbar } from "../Scrollbar";
import { Text, defaultTextVariants } from "../Text";
import { Tooltip } from "../Tooltip";
import { TrackingPlayButton } from "../TrackingPlayButton";
import { TruncatedText } from "../TruncatedText";
import {
  CompositeListActionSlot,
  CompositeListPrimaryButton,
  CompositeListRow,
  CompositeListSurface,
  useCompositeListNavigation,
} from "../foundations/compositeList";
import { controlFocusRing } from "../foundations/controlStyles";
import { interactiveSurfaceCss } from "../foundations/interactiveSurface";
import { yScrollbarStyles } from "../foundations/scrollbar";
import { SrOnly } from "../foundations/srOnly";
import {
  annotationStudioNewSequenceSummary,
  annotationStudioSequenceDirectoryCount,
  annotationStudioText,
} from "./messages";
import type {
  AnnotationSequence,
  AnnotationSequenceDirectorySnapshot,
  AnnotationStream,
  AnnotationStudioLabels,
  AnnotationStudioLocale,
  AnnotationStudioSequenceActionPresentation,
  AnnotationStudioSequenceActionRenderer,
  AnnotationStudioSequenceActionResult,
  SequenceDirectoryContentScope,
  TrackingPreviewStatus,
  TrackingPreviewTargetMedia,
} from "./types";
import { STUDIO_BREAKPOINT, STUDIO_RAIL_WIDTH } from "./MediaAnnotationStudioConstraint";

export interface StudioContextRailProps {
  readonly contextLead?: ReactNode;
  readonly locale: AnnotationStudioLocale;
  readonly labels: AnnotationStudioLabels;
  readonly sequences: readonly AnnotationSequence[];
  readonly sequenceDirectory?: AnnotationSequenceDirectorySnapshot | null;
  readonly pinnedSequenceId?: string | null;
  readonly onSequenceDirectoryQueryChange?: (
    query: AnnotationSequenceDirectorySnapshot["query"],
  ) => void;
  readonly onLoadNextSequencePage?: () => void;
  readonly onRetrySequenceDirectory?: () => void;
  readonly newSequenceIds: ReadonlySet<string>;
  readonly globalStreams: readonly AnnotationStream[];
  readonly selectedSequenceId: string | null;
  readonly selectedStreamId: string | null;
  readonly dirtyFrameCountsByStream: ReadonlyMap<string, number>;
  readonly dirtyFrameCountsBySequenceAndStream: ReadonlyMap<string, ReadonlyMap<string, number>>;
  readonly saveConflictFrameCountsByStream: ReadonlyMap<string, number>;
  readonly saveConflictFrameCountsBySequenceAndStream: ReadonlyMap<
    string,
    ReadonlyMap<string, number>
  >;
  readonly validationIssueFrameCountsByStream: ReadonlyMap<string, number>;
  readonly validationIssueFrameCountsBySequenceAndStream: ReadonlyMap<
    string,
    ReadonlyMap<string, number>
  >;
  readonly capturedFrameCountsByStream: ReadonlyMap<string, number>;
  readonly savedSequenceContentCountsByStream?: Readonly<{
    captured: ReadonlyMap<string, number>;
    annotated: ReadonlyMap<string, number>;
  }> | null;
  readonly unannotatedFrameCountsBySequenceAndStream: ReadonlyMap<
    string,
    ReadonlyMap<string, number>
  >;
  readonly annotatedFrameCountsBySequenceAndStream: ReadonlyMap<
    string,
    ReadonlyMap<string, number>
  >;
  readonly trackableSequenceIds: ReadonlySet<string>;
  readonly trackableStreamIds: ReadonlySet<string>;
  readonly runnableTrackingSequenceIds: ReadonlySet<string>;
  readonly trackingStartBlocked: boolean;
  readonly trackingPreviewTargetMedia: TrackingPreviewTargetMedia | null;
  readonly trackingPreviewStatus: TrackingPreviewStatus;
  readonly trackingPreviewErrorMessage?: string;
  readonly renderSequenceAction?: AnnotationStudioSequenceActionRenderer;
  readonly onSelectSequence: (sequenceId: string) => void;
  readonly onSelectStream: (streamId: string) => void;
  readonly onOpenSequenceInVideo: (sequenceId: string) => void;
  readonly onOpenStreamInVideo: (streamId: string) => void;
  readonly onStartTrackingPreview: (sequenceId: string) => void;
  readonly onStopTrackingPreview: () => void;
}

const NEW_SEQUENCE_CHIP_COLOR = 0x16a34a;

function isSequenceActionPresentation(
  action: AnnotationStudioSequenceActionResult,
): action is AnnotationStudioSequenceActionPresentation {
  if (typeof action !== "object" || action === null) return false;
  const candidate = action as Partial<AnnotationStudioSequenceActionPresentation>;
  return candidate.mode === "available" || (candidate.mode === "persistent" && "action" in action);
}

function resolveSequenceAction(
  action: AnnotationStudioSequenceActionResult | undefined,
): AnnotationStudioSequenceActionPresentation | null {
  if (action === undefined) return null;
  if (isSequenceActionPresentation(action)) {
    if (action.mode === "available") {
      return isRenderableNode(action.action) ? action : { mode: "available" };
    }
    return isRenderableNode(action.action) ? action : null;
  }
  return isRenderableNode(action) ? { action, mode: "persistent" } : null;
}

export const StudioWorkspace = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;
  flex: 1;
  grid-template-columns:
    clamp(${STUDIO_RAIL_WIDTH.min}, ${STUDIO_RAIL_WIDTH.ideal}, ${STUDIO_RAIL_WIDTH.max})
    minmax(0, 1fr);

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
    ${yScrollbarStyles}
  }
`;

const Root = styled.aside`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.65rem;
  border-right: 1px solid ${({ theme }) => theme.color.border.divider};
  background: ${({ theme }) => theme.color.bg.surfaceSunken};

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: stretch;
    min-height: 12rem;
    border-right: 0;
    border-bottom: 1px solid ${({ theme }) => theme.color.border.divider};
  }

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.tightCommandBar}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// 제품 선택 영역이 커져도 카메라·에피소드 탐색 공간을 남긴다.
const ContextLead = styled.div`
  min-width: 0;
  min-height: 0;
  max-height: min(12rem, 30%);
  flex: 0 1 auto;
  grid-column: 1 / -1;
  ${yScrollbarStyles}

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    max-height: 12rem;
  }
`;

const ListSection = styled.div`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 0.35rem;
`;

const ListSectionHeading = styled.div`
  display: flex;
  min-width: 0;
  min-height: 1rem;
  align-items: center;
  gap: 0.35rem;
`;

const EpisodeDirectoryHeader = styled.div`
  display: grid;
  min-width: 0;
  gap: 0.35rem;
`;

const EpisodeDirectoryControls = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
`;

const EpisodeDirectoryFilter = styled(Filter)`
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.25rem;

  /* 두 facet label은 locale과 선택값에 따라 길이가 달라 한 줄 압축 시 의미가 잘린다.
     Filter의 public API를 Studio layout 요구로 넓히지 않고 wrapper composition으로 세로 배치한다. */
  & > div {
    width: 100%;
    min-width: 0;
    grid-column: 1;
  }

  & > div > button {
    width: 100%;
  }

  & > [data-c6-tooltip-trigger] {
    grid-row: 1 / span 2;
    grid-column: 2;
  }
`;

const EpisodeResultCount = styled(Text)`
  margin-left: auto;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.text.subtle};
`;

const CameraListSection = styled(ListSection)`
  max-height: 18rem;
  flex: 0 1 auto;

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    max-height: none;
    min-height: 12rem;
    flex: 1;
  }
`;

const ListViewport = styled.div`
  display: flex;
  min-width: 0;
  min-height: 8rem;
  flex: 1;
`;

const EpisodeResultsViewport = styled(ListViewport)`
  flex-direction: column;
`;

const CameraListViewport = styled(ListViewport)`
  min-height: 0;
  flex: 0 1 auto;

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    min-height: 8rem;
    flex: 1;
  }
`;

const CameraListScroller = styled(Scrollbar)`
  width: 100%;
  min-width: 0;
  min-height: 0;
  flex: 0 1 auto;

  @container media-annotation-studio (max-width: ${STUDIO_BREAKPOINT.stackedRail}) {
    flex: 1;
  }
`;

const ContextListSurface = styled(CompositeListSurface)``;

const ContextListEmpty = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 1rem;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.surface};
  background: ${({ theme }) => theme.color.bg.surfaceRaised};
  color: ${({ theme }) => theme.color.text.muted};
  text-align: center;
`;

const DirectoryStatus = styled.div`
  display: flex;
  min-height: 5rem;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.75rem;
  color: ${({ theme }) => theme.color.text.muted};
  text-align: center;
`;

const DirectoryRetryButton = styled.button`
  padding: 0.3rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  font: inherit;
  cursor: pointer;
  ${({ theme }) =>
    interactiveSurfaceCss({
      rest: {
        background: theme.color.bg.surfaceRaisedHigh,
        border: theme.color.border.default,
        foreground: theme.color.text.default,
      },
      hover: {
        background: theme.color.bg.surfaceOverlay,
        border: theme.color.border.default,
        foreground: theme.color.text.default,
      },
      active: {
        background: theme.color.bg.selectedActive,
        border: theme.color.border.default,
        foreground: theme.color.text.default,
      },
      disabled: {
        background: theme.color.bg.control.disabledSunken,
        border: theme.color.border.disabled,
        foreground: theme.color.text.disabled,
      },
    })}

  &:focus-visible {
    ${controlFocusRing("2px")}
  }
`;

const CameraRow = styled(CompositeListRow).attrs({ $size: "regular" as const })`
  grid-template-columns: minmax(0, 1fr);
`;

const EpisodeTrackingActionSlot = styled(CompositeListActionSlot).attrs({
  $size: "regular" as const,
})`
  position: relative;

  & > span {
    align-items: center;
    justify-content: center;
  }
`;

const EpisodeStatusLayer = styled.span<{ $actionable: boolean }>`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;

  ${({ $actionable }) =>
    $actionable &&
    css`
      @media (hover: none) {
        opacity: 0;
        pointer-events: none;
      }
    `}
`;

const ContextMarkCanvas = styled.span`
  display: inline-flex;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  align-items: center;
  justify-content: center;
`;

const EpisodeIncompleteMark = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  border: 1px solid ${({ theme }) => theme.color.text.subtle};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background: transparent;

  @media (forced-colors: active) {
    border-color: ButtonText;
  }
`;

// tracking 실행 중/실패 컨트롤은 원형으로 — 사각 실루엣이면 옆 status 마크(무지개/미완료
// 사각형)와 같은 부류로 읽혀 버튼 어포던스가 죽는다.
const EpisodeTrackingControlButton = styled(IconButton)`
  border-radius: 50%;
`;

const episodeAvailableActionLayerStyles = css`
  position: absolute;
  inset: 0;
  display: flex;
  opacity: 0;
  pointer-events: none;

  & > span {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: stretch;
  }

  @media (hover: none) {
    opacity: 1;
    pointer-events: auto;
  }
`;

const EpisodeTrackingStartLayer = styled.span`
  ${episodeAvailableActionLayerStyles}
`;

const EpisodeAvailableActionLayer = styled.span`
  ${episodeAvailableActionLayerStyles}
`;

const EpisodeTrackingPlayButton = styled(TrackingPlayButton)`
  &&:not(:disabled) {
    animation-play-state: paused;
  }
`;

type AnnotationSequenceListViewProps = ListViewProps<AnnotationSequence> & {
  readonly $trackingStartBlocked: boolean;
};

function AnnotationSequenceListView(props: AnnotationSequenceListViewProps) {
  return <ListView {...props} />;
}

const EpisodeListView = styled(AnnotationSequenceListView)`
  min-width: 0;
  min-height: 0;
  flex: 1;

  .content {
    padding: 0;
  }

  & li:hover ${EpisodeTrackingActionSlot}, & li:focus-within ${EpisodeTrackingActionSlot} {
    visibility: visible;
    opacity: 1;
    pointer-events: auto;
  }

  ${({ $trackingStartBlocked }) =>
    $trackingStartBlocked
      ? css`
          ${EpisodeStatusLayer}[data-legacy-tracking-status-layer] {
            opacity: 1;
            pointer-events: none;
          }

          ${EpisodeTrackingStartLayer} {
            visibility: hidden;
            opacity: 0;
            pointer-events: none;
          }
        `
      : css`
          & li:hover
            ${EpisodeStatusLayer}[data-legacy-tracking-status-layer][data-actionable="true"],
          & li:focus-within
            ${EpisodeStatusLayer}[data-legacy-tracking-status-layer][data-actionable="true"] {
            opacity: 0;
            pointer-events: none;
          }

          & li:hover ${EpisodeTrackingStartLayer}, & li:focus-within ${EpisodeTrackingStartLayer} {
            opacity: 1;
            pointer-events: auto;
          }

          & li:hover ${EpisodeTrackingPlayButton}:not(:disabled),
          & li:focus-within ${EpisodeTrackingPlayButton}:not(:disabled) {
            animation-play-state: running;
          }
        `}

  & li:hover ${EpisodeStatusLayer}[data-sequence-available-status-layer][data-actionable="true"],
  & li:focus-within
    ${EpisodeStatusLayer}[data-sequence-available-status-layer][data-actionable="true"] {
    opacity: 0;
    pointer-events: none;
  }

  & li:hover ${EpisodeAvailableActionLayer}, & li:focus-within ${EpisodeAvailableActionLayer} {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ASYNC_EPISODE_OVERSCAN = 5;
const SEQUENCE_CONTENT_FILTER_GROUP = "sequence-content";
const SEQUENCE_STATUS_FILTER_GROUP = "sequence-status";
const NEEDS_ATTENTION_FILTER = "needs-attention";

function sameSequenceIdScope(
  left: readonly string[] | undefined,
  right: readonly string[] | undefined,
): boolean {
  if (left === right) return true;
  if (!left || !right || left.length !== right.length) return false;
  return left.every((sequenceId, index) => sequenceId === right[index]);
}

function createSequenceDirectoryQuery(
  streamId: string | null,
  contentScope: SequenceDirectoryContentScope,
  attentionSequenceIds?: readonly string[],
): AnnotationSequenceDirectorySnapshot["query"] {
  return {
    streamId,
    contentScope,
    ...(attentionSequenceIds === undefined ? {} : { attentionSequenceIds }),
  };
}

const ContextPrimaryButton = styled(CompositeListPrimaryButton).attrs({
  $size: "regular" as const,
})`
  display: flex;
  align-items: stretch;
  padding: 0.38rem 0.55rem;
`;

const EpisodePrimaryContent = styled.span`
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) 3.5rem;
  align-items: stretch;
`;

const ItemIdentity = styled.span`
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 0.05rem;
`;

const CameraItemIdentity = styled(ItemIdentity)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 2rem;
  grid-template-rows: auto auto;
  align-items: stretch;
  gap: 0.2rem 0;
`;

const EpisodeItemIdentity = styled(ItemIdentity)`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto auto;
  align-items: stretch;
  gap: 0.2rem 0;
  padding: 0.38rem 0.2rem 0.38rem 0.55rem;
`;

const ItemTitleRow = styled.span`
  display: inline-flex;
  min-width: 0;
  max-width: 100%;
  align-self: flex-start;
  align-items: center;
  gap: 0.3rem;
`;

const NewSequenceChip = styled(LabelChip)`
  flex: 0 0 auto;
`;

const NewEpisodeBadge = styled(NewSequenceChip)``;

const NewEpisodeSummaryChip = styled(NewSequenceChip)``;

const EpisodeTitleRow = styled(ItemTitleRow)`
  grid-row: 1;
  grid-column: 1;
`;

const CameraTitleRow = styled(ItemTitleRow)`
  grid-row: 1;
  grid-column: 1;
`;

const ItemName = styled(TruncatedText)<{ $selected: boolean }>`
  display: block;
  min-width: 0;
  flex: 0 1 auto;
  color: inherit;
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.weight.bold : theme.typography.weight.medium};
`;

const ContextStatusAnchor = styled.span`
  display: inline-flex;
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 0.875rem;
`;

const TrackingAvailabilityMark = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  width: 12px;
  height: 12px;
  flex: 0 0 12px;
  border: 1px solid ${({ theme }) => theme.color.border.default};
  border-radius: ${({ theme }) => theme.shape.radius.control};
  background-color: ${({ theme }) => theme.color.bg.surfaceSunken};
  background-image: ${({ theme }) => theme.color.bg.control.trackingPlay};

  @media (forced-colors: active) {
    border: 1px solid ButtonText;
    background-color: ButtonFace;
    background-image: repeating-linear-gradient(135deg, ButtonText 0 1px, ButtonFace 1px 3px);
  }
`;

const ContextMetrics = styled.span`
  display: inline-flex;
  min-width: 0;
  min-height: 0.875rem;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
`;

const CameraMetrics = styled(ContextMetrics)`
  grid-row: 2;
  grid-column: 1;
`;

const EpisodeMetrics = styled(ContextMetrics)`
  grid-row: 2;
  grid-column: 1;
`;

const CameraTrackingStatusSlot = styled.span`
  display: flex;
  width: 2rem;
  min-width: 2rem;
  grid-row: 1 / span 2;
  grid-column: 2;
  align-items: center;
  justify-content: center;

  & [data-camera-tracking-available] {
    width: 16px;
    height: 16px;
    flex-basis: 16px;
  }
`;

const EpisodeAnnotationStatus = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.2rem;
  color: ${({ theme }) => theme.color.text.default};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  font-variant-numeric: tabular-nums;
  line-height: 1;

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.8rem;
  }

  & > span {
    min-width: 0;
  }
`;

const FrameCountStatus = styled(EpisodeAnnotationStatus)<{ $empty: boolean }>`
  justify-self: end;
  color: ${({ theme }) => theme.color.text.subtle};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

const UnannotatedFrameCountStatus = styled(FrameCountStatus)<{ $empty: boolean }>`
  color: ${({ $empty, theme }) => ($empty ? theme.color.text.subtle : theme.color.text.default)};

  & [data-unannotated-frame-count-icon] {
    color: inherit;
  }
`;

const AnnotationFrameCountStatus = styled(FrameCountStatus)<{ $empty: boolean }>`
  color: ${({ $empty, theme }) => ($empty ? theme.color.text.subtle : theme.color.text.default)};
  font-weight: ${({ $empty, theme }) =>
    $empty ? theme.typography.weight.regular : theme.typography.weight.semibold};

  & [data-annotation-frame-count-icon] {
    color: inherit;
  }
`;

const EpisodeDurationStatus = styled(EpisodeAnnotationStatus)`
  width: 100%;
  height: 100%;
  grid-column: 2;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.text.subtle};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

const ContextMetricIconTooltip = styled(Tooltip)`
  width: 0.875rem;
  height: 0.875rem;
  flex: 0 0 auto;
  justify-content: center;

  & > svg {
    width: 0.875rem;
    height: 0.875rem;
    font-size: 0.8rem;
  }
`;

function framesWithoutAnnotationLabel(count: number, locale: AnnotationStudioLocale): string {
  return `${annotationStudioText("framesWithoutAnnotation", locale)}: ${count}`;
}

function framesWithAnnotationLabel(count: number, locale: AnnotationStudioLocale): string {
  return `${annotationStudioText("framesWithAnnotation", locale)}: ${count}`;
}

function episodesLabel(
  count: number | null,
  labels: AnnotationStudioLabels,
  locale: AnnotationStudioLocale,
): string {
  return `${labels.sequence[locale]}: ${count ?? annotationStudioText("countUnavailable", locale)}`;
}

function episodeDurationLabel(duration: string, locale: AnnotationStudioLocale): string {
  return `${annotationStudioText("episodeDuration", locale)}: ${duration}`;
}

function formatEpisodeDuration(
  durationSeconds: number | null | undefined,
  locale: AnnotationStudioLocale,
): Readonly<{ display: string; accessible: string }> | null {
  if (
    durationSeconds === null ||
    durationSeconds === undefined ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds < 0
  ) {
    return null;
  }

  const roundedSeconds = Math.round(durationSeconds);
  const minutes = Math.floor(roundedSeconds / 60);
  const seconds = roundedSeconds % 60;
  const display = minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, "0")}s` : `${seconds}s`;
  const accessible =
    locale === "ko"
      ? minutes > 0
        ? `${minutes}분 ${seconds}초`
        : `${seconds}초`
      : minutes > 0
        ? `${minutes} ${minutes === 1 ? "minute" : "minutes"} ${seconds} ${seconds === 1 ? "second" : "seconds"}`
        : `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  return { display, accessible };
}

function contextStatusLabel({
  locale,
  dirtyFrameCount,
  saveConflictFrameCount,
  validationIssueFrameCount,
}: {
  locale: AnnotationStudioLocale;
  dirtyFrameCount: number;
  saveConflictFrameCount: number;
  validationIssueFrameCount: number;
}): string {
  if (saveConflictFrameCount === 0 && validationIssueFrameCount === 0) {
    return annotationStudioText("unsaved", locale);
  }

  return [
    validationIssueFrameCount > 0
      ? `${annotationStudioText("framesNeedingAttention", locale)}: ${validationIssueFrameCount}`
      : null,
    saveConflictFrameCount > 0
      ? `${annotationStudioText("saveConflicts", locale)}: ${saveConflictFrameCount}`
      : null,
    dirtyFrameCount > 0
      ? `${annotationStudioText("unsavedFrames", locale)}: ${dirtyFrameCount}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function TrackingAvailabilityIndicator({
  context,
  contextId,
  locale,
}: {
  context: "camera" | "episode";
  contextId: string;
  locale: AnnotationStudioLocale;
}) {
  const label = annotationStudioText("trackingAvailable", locale);
  return (
    <Tooltip content={label} placement="right" describeTrigger={false}>
      <ContextMarkCanvas role="img" aria-label={label}>
        <TrackingAvailabilityMark
          aria-hidden="true"
          data-context-tracking-available={context}
          {...(context === "camera"
            ? { "data-camera-tracking-available": contextId }
            : { "data-episode-tracking-available": contextId })}
        />
      </ContextMarkCanvas>
    </Tooltip>
  );
}

function EpisodeIncompleteIndicator({
  count,
  episodeId,
  locale,
}: {
  count: number;
  episodeId: string;
  locale: AnnotationStudioLocale;
}) {
  const label = framesWithoutAnnotationLabel(count, locale);
  return (
    <Tooltip content={label} placement="right" describeTrigger={false}>
      <ContextMarkCanvas role="img" aria-label={label}>
        <EpisodeIncompleteMark aria-hidden="true" data-episode-incomplete-indicator={episodeId} />
      </ContextMarkCanvas>
    </Tooltip>
  );
}

function ContextStatusIndicator({
  context,
  contextId,
  locale,
  dirtyFrameCount,
  saveConflictFrameCount,
  validationIssueFrameCount,
}: {
  context: "camera" | "episode";
  contextId: string;
  locale: AnnotationStudioLocale;
  dirtyFrameCount: number;
  saveConflictFrameCount: number;
  validationIssueFrameCount: number;
}) {
  const status = saveConflictFrameCount > 0 || validationIssueFrameCount > 0 ? "conflict" : "dirty";
  const label = contextStatusLabel({
    locale,
    dirtyFrameCount,
    saveConflictFrameCount,
    validationIssueFrameCount,
  });

  return (
    <Tooltip content={label} placement="right" describeTrigger={false}>
      <NotificationBadge
        visible
        tone={status === "conflict" ? "error" : "warning"}
        size="0.45rem"
        offset="0.05rem"
        aria-label={label}
      >
        <ContextStatusAnchor
          data-context-status-indicator={status}
          data-context-indicator-tone={status === "conflict" ? "error" : "warning"}
          data-context-dirty-indicator={dirtyFrameCount > 0 ? context : undefined}
          data-context-save-conflict-indicator={saveConflictFrameCount > 0 ? context : undefined}
          data-context-validation-conflict-indicator={
            validationIssueFrameCount > 0 ? context : undefined
          }
          {...(context === "camera"
            ? {
                "data-camera-dirty-indicator": dirtyFrameCount > 0 ? contextId : undefined,
                "data-camera-save-conflict-indicator":
                  saveConflictFrameCount > 0 ? contextId : undefined,
                "data-camera-validation-conflict-indicator":
                  validationIssueFrameCount > 0 ? contextId : undefined,
              }
            : {
                "data-episode-dirty-indicator": dirtyFrameCount > 0 ? contextId : undefined,
                "data-episode-save-conflict-indicator":
                  saveConflictFrameCount > 0 ? contextId : undefined,
                "data-episode-validation-conflict-indicator":
                  validationIssueFrameCount > 0 ? contextId : undefined,
              })}
          aria-hidden="true"
        />
      </NotificationBadge>
    </Tooltip>
  );
}

interface EpisodeContextItemProps {
  readonly item: AnnotationSequence;
  readonly selected: boolean;
  readonly newlyAdded: boolean;
  readonly locale: AnnotationStudioLocale;
  readonly selectedStreamLabel: string | null;
  readonly unannotatedFrameCount: number;
  readonly annotatedFrameCount: number;
  readonly dirtyFrameCount: number;
  readonly saveConflictFrameCount: number;
  readonly validationIssueFrameCount: number;
  readonly trackingAvailable: boolean;
  readonly trackingRunnable: boolean;
  readonly trackingState: "idle" | "running" | "paused" | "failed";
  readonly trackingErrorMessage?: string;
  readonly sequenceActionConfigured: boolean;
  readonly sequenceAction?: AnnotationStudioSequenceActionResult;
  readonly pinned?: boolean;
  readonly onStartTrackingPreview: (sequenceId: string) => void;
  readonly onStopTrackingPreview: () => void;
}

const EpisodeContextContent = memo(function EpisodeContextContent({
  item,
  selected,
  newlyAdded,
  locale,
  selectedStreamLabel,
  unannotatedFrameCount,
  annotatedFrameCount,
  dirtyFrameCount,
  saveConflictFrameCount,
  validationIssueFrameCount,
  pinned = false,
}: EpisodeContextItemProps) {
  const duration = formatEpisodeDuration(item.durationSeconds, locale);
  const trackingConflict = saveConflictFrameCount > 0 || validationIssueFrameCount > 0;
  const unannotatedFrameLabel = [
    selectedStreamLabel,
    framesWithoutAnnotationLabel(unannotatedFrameCount, locale),
  ]
    .filter(Boolean)
    .join(". ");
  const annotatedFrameLabel = [
    selectedStreamLabel,
    framesWithAnnotationLabel(annotatedFrameCount, locale),
  ]
    .filter(Boolean)
    .join(". ");

  return (
    <EpisodePrimaryContent
      data-studio-context-row="episode"
      data-episode-pinned={pinned || undefined}
      data-episode-select={item.id}
    >
      <EpisodeItemIdentity
        data-episode-id={item.id}
        data-episode-new={newlyAdded || undefined}
        data-episode-duration-seconds={duration === null ? undefined : item.durationSeconds}
        data-episode-unannotated-frame-count={unannotatedFrameCount}
        data-episode-annotated-frame-count={annotatedFrameCount}
      >
        <EpisodeTitleRow data-context-item-title="">
          <ItemName $selected={selected} fullText={item.label}>
            {item.label}
          </ItemName>
          {dirtyFrameCount > 0 || trackingConflict ? (
            <ContextStatusIndicator
              context="episode"
              contextId={item.id}
              locale={locale}
              dirtyFrameCount={dirtyFrameCount}
              saveConflictFrameCount={saveConflictFrameCount}
              validationIssueFrameCount={validationIssueFrameCount}
            />
          ) : null}
        </EpisodeTitleRow>
        <EpisodeMetrics data-episode-metrics="">
          <UnannotatedFrameCountStatus
            $empty={unannotatedFrameCount === 0}
            role="img"
            aria-label={unannotatedFrameLabel}
            data-episode-unannotated-frame-count-indicator=""
          >
            <FrameIcon data-unannotated-frame-count-icon="" aria-hidden="true" />
            <span aria-hidden="true">{unannotatedFrameCount}</span>
          </UnannotatedFrameCountStatus>
          <AnnotationFrameCountStatus
            $empty={annotatedFrameCount === 0}
            role="img"
            aria-label={annotatedFrameLabel}
            data-episode-annotation-status={annotatedFrameCount > 0 ? "annotated" : "empty"}
            data-episode-annotated-frame-count-indicator=""
          >
            <AnnotationIcon data-annotation-frame-count-icon="" aria-hidden="true" />
            <span aria-hidden="true">{annotatedFrameCount}</span>
          </AnnotationFrameCountStatus>
          {newlyAdded ? (
            <NewEpisodeBadge
              color={NEW_SEQUENCE_CHIP_COLOR}
              size="xs"
              variant="outline"
              showIcon={false}
              role="img"
              aria-label={annotationStudioText("newSequence", locale)}
              data-new-episode-badge=""
            >
              <span aria-hidden="true">N</span>
            </NewEpisodeBadge>
          ) : null}
        </EpisodeMetrics>
      </EpisodeItemIdentity>
      {duration !== null ? (
        <EpisodeDurationStatus data-episode-duration-indicator="">
          <SrOnly>{episodeDurationLabel(duration.accessible, locale)}</SrOnly>
          <span aria-hidden="true">{duration.display}</span>
        </EpisodeDurationStatus>
      ) : null}
      {pinned ? (
        <SrOnly>{annotationStudioText("selectedEpisodeOutsideFilter", locale)}</SrOnly>
      ) : null}
    </EpisodePrimaryContent>
  );
});

const EpisodeContextActions = memo(function EpisodeContextActions({
  item,
  locale,
  unannotatedFrameCount,
  dirtyFrameCount,
  saveConflictFrameCount,
  validationIssueFrameCount,
  trackingAvailable,
  trackingRunnable,
  trackingState,
  trackingErrorMessage,
  sequenceActionConfigured,
  sequenceAction,
  onStartTrackingPreview,
  onStopTrackingPreview,
}: EpisodeContextItemProps) {
  const trackingRunning = trackingState === "running";
  const trackingPaused = trackingState === "paused";
  const trackingFailed = trackingState === "failed";
  const trackingConflict = saveConflictFrameCount > 0 || validationIssueFrameCount > 0;
  const trackingStatusIncomplete = unannotatedFrameCount > 0;
  const hasEpisodeStatus = trackingStatusIncomplete || trackingAvailable;
  const resolvedSequenceAction = resolveSequenceAction(sequenceAction);
  const availableSequenceAction =
    resolvedSequenceAction?.mode === "available" && isRenderableNode(resolvedSequenceAction.action)
      ? resolvedSequenceAction.action
      : null;
  const actionSlotVisible = sequenceActionConfigured
    ? resolvedSequenceAction !== null
    : hasEpisodeStatus || trackingRunning || trackingFailed;
  const trackingConflictData = trackingConflict
    ? {
        "data-context-status-indicator": "conflict",
        "data-context-indicator-tone": "error",
        "data-episode-dirty-indicator": dirtyFrameCount > 0 ? item.id : undefined,
        "data-episode-save-conflict-indicator": saveConflictFrameCount > 0 ? item.id : undefined,
        "data-episode-validation-conflict-indicator":
          validationIssueFrameCount > 0 ? item.id : undefined,
      }
    : {};

  return (
    <EpisodeTrackingActionSlot
      $visible={actionSlotVisible}
      data-episode-tracking-action-slot=""
      data-episode-status-action-slot=""
      data-sequence-action-mode={resolvedSequenceAction?.mode}
      data-episode-tracking-active={trackingRunning || undefined}
      data-episode-tracking-failed={trackingFailed || undefined}
    >
      {sequenceActionConfigured ? (
        resolvedSequenceAction?.mode === "persistent" ? (
          resolvedSequenceAction.action
        ) : resolvedSequenceAction?.mode === "available" ? (
          <>
            <EpisodeStatusLayer
              $actionable={availableSequenceAction !== null}
              data-actionable={availableSequenceAction !== null ? "true" : undefined}
              aria-hidden={availableSequenceAction !== null ? "true" : undefined}
              data-episode-status-layer={item.id}
              data-sequence-available-status-layer={item.id}
            >
              <TrackingAvailabilityIndicator
                context="episode"
                contextId={item.id}
                locale={locale}
              />
            </EpisodeStatusLayer>
            {availableSequenceAction !== null ? (
              <EpisodeAvailableActionLayer data-sequence-available-action-layer={item.id}>
                {availableSequenceAction}
              </EpisodeAvailableActionLayer>
            ) : null}
          </>
        ) : null
      ) : trackingRunning ? (
        <Tooltip
          key="episode-tracking-pause"
          content={annotationStudioText("pauseTracking", locale)}
          placement="right"
        >
          <EpisodeTrackingControlButton
            {...trackingConflictData}
            data-episode-tracking-action={item.id}
            data-episode-tracking-available={trackingAvailable ? item.id : undefined}
            data-episode-tracking-pause={item.id}
            data-studio-list-warning=""
            size="sm"
            variant="solid"
            tone="warning"
            aria-label={annotationStudioText("pauseTracking", locale)}
            onClick={onStopTrackingPreview}
          >
            <PauseIcon aria-hidden="true" />
          </EpisodeTrackingControlButton>
        </Tooltip>
      ) : trackingFailed ? (
        <Tooltip
          key="episode-tracking-retry"
          content={
            annotationStudioText("retryTracking", locale) +
            (trackingErrorMessage ? " · " + trackingErrorMessage : "")
          }
          placement="right"
        >
          <EpisodeTrackingControlButton
            {...trackingConflictData}
            data-episode-tracking-action={item.id}
            data-episode-tracking-available={trackingAvailable ? item.id : undefined}
            data-episode-tracking-retry={item.id}
            size="sm"
            variant="surface"
            tone="warning"
            aria-label={annotationStudioText("retryTracking", locale)}
            onClick={() => onStartTrackingPreview(item.id)}
          >
            <RotateCcwIcon aria-hidden="true" />
          </EpisodeTrackingControlButton>
        </Tooltip>
      ) : hasEpisodeStatus ? (
        <>
          <EpisodeStatusLayer
            $actionable={trackingAvailable && trackingRunnable}
            data-actionable={trackingAvailable && trackingRunnable ? "true" : undefined}
            aria-hidden={trackingAvailable ? "true" : undefined}
            data-episode-status-layer={item.id}
            data-legacy-tracking-status-layer=""
          >
            {trackingAvailable ? (
              <TrackingAvailabilityIndicator
                context="episode"
                contextId={item.id}
                locale={locale}
              />
            ) : trackingStatusIncomplete ? (
              <EpisodeIncompleteIndicator
                count={unannotatedFrameCount}
                episodeId={item.id}
                locale={locale}
              />
            ) : null}
          </EpisodeStatusLayer>
          {trackingAvailable && trackingRunnable ? (
            <EpisodeTrackingStartLayer data-episode-tracking-start-layer={item.id}>
              <Tooltip
                content={annotationStudioText(trackingPaused ? "resumeTracking" : "track", locale)}
                placement="right"
              >
                <EpisodeTrackingPlayButton
                  data-episode-tracking-action={item.id}
                  data-episode-tracking-start={item.id}
                  data-episode-tracking-resume={trackingPaused ? item.id : undefined}
                  size="sm"
                  aria-label={annotationStudioText(
                    trackingPaused ? "resumeTracking" : "track",
                    locale,
                  )}
                  onClick={() => onStartTrackingPreview(item.id)}
                >
                  <PlayIcon aria-hidden="true" />
                </EpisodeTrackingPlayButton>
              </Tooltip>
            </EpisodeTrackingStartLayer>
          ) : null}
        </>
      ) : null}
    </EpisodeTrackingActionSlot>
  );
});

function StudioContextRailComponent({
  contextLead,
  locale,
  labels,
  sequences,
  sequenceDirectory,
  pinnedSequenceId,
  onSequenceDirectoryQueryChange,
  onLoadNextSequencePage,
  onRetrySequenceDirectory,
  newSequenceIds,
  globalStreams,
  selectedSequenceId,
  selectedStreamId,
  dirtyFrameCountsByStream,
  dirtyFrameCountsBySequenceAndStream,
  saveConflictFrameCountsByStream,
  saveConflictFrameCountsBySequenceAndStream,
  validationIssueFrameCountsByStream,
  validationIssueFrameCountsBySequenceAndStream,
  capturedFrameCountsByStream,
  savedSequenceContentCountsByStream,
  unannotatedFrameCountsBySequenceAndStream,
  annotatedFrameCountsBySequenceAndStream,
  trackableSequenceIds,
  trackableStreamIds,
  runnableTrackingSequenceIds,
  trackingStartBlocked,
  trackingPreviewTargetMedia,
  trackingPreviewStatus,
  trackingPreviewErrorMessage,
  renderSequenceAction,
  onSelectSequence,
  onSelectStream,
  onOpenSequenceInVideo,
  onOpenStreamInVideo,
  onStartTrackingPreview,
  onStopTrackingPreview,
}: StudioContextRailProps) {
  const cameraMetricsId = useId();
  const cameraScrollerRef = useRef<HTMLDivElement>(null);
  const selectedStream = globalStreams.find((candidate) => candidate.id === selectedStreamId);
  const asyncDirectoryEnabled = sequenceDirectory !== null && sequenceDirectory !== undefined;
  const pinnedSequence =
    asyncDirectoryEnabled && pinnedSequenceId
      ? (sequences.find((candidate) => candidate.id === pinnedSequenceId) ?? null)
      : null;
  const directorySequences = useMemo(
    () =>
      asyncDirectoryEnabled && selectedStreamId !== null
        ? sequenceDirectory.items.filter((candidate) =>
            candidate.streamIds.includes(selectedStreamId),
          )
        : [],
    [asyncDirectoryEnabled, selectedStreamId, sequenceDirectory],
  );
  const staticScopedSequences =
    selectedStreamId === null
      ? []
      : asyncDirectoryEnabled
        ? []
        : sequences.filter((candidate) => candidate.streamIds.includes(selectedStreamId));
  const displayedSequenceResults = asyncDirectoryEnabled
    ? directorySequences
    : staticScopedSequences;
  const scopedNewSequenceCount = displayedSequenceResults.reduce(
    (count, sequence) => count + (newSequenceIds.has(sequence.id) ? 1 : 0),
    0,
  );
  const totalUnannotatedFramesByStream = useMemo(() => {
    const totals = new Map<string, number>();
    for (const counts of unannotatedFrameCountsBySequenceAndStream.values()) {
      for (const [streamId, count] of counts) {
        totals.set(streamId, (totals.get(streamId) ?? 0) + count);
      }
    }
    return totals;
  }, [unannotatedFrameCountsBySequenceAndStream]);
  const totalAnnotatedFramesByStream = useMemo(() => {
    const totals = new Map<string, number>();
    for (const counts of annotatedFrameCountsBySequenceAndStream.values()) {
      for (const [streamId, count] of counts) {
        totals.set(streamId, (totals.get(streamId) ?? 0) + count);
      }
    }
    return totals;
  }, [annotatedFrameCountsBySequenceAndStream]);
  const sequenceContentCounts = useMemo(
    () =>
      selectedStreamId === null || !savedSequenceContentCountsByStream
        ? null
        : {
            captured: savedSequenceContentCountsByStream.captured.get(selectedStreamId) ?? 0,
            annotated: savedSequenceContentCountsByStream.annotated.get(selectedStreamId) ?? 0,
          },
    [savedSequenceContentCountsByStream, selectedStreamId],
  );
  const attentionSequenceIds = useMemo(() => {
    if (selectedStreamId === null) return [];
    const sequenceIds = new Set<string>();
    for (const countsBySequence of [
      dirtyFrameCountsBySequenceAndStream,
      saveConflictFrameCountsBySequenceAndStream,
      validationIssueFrameCountsBySequenceAndStream,
    ]) {
      for (const [sequenceId, countsByStream] of countsBySequence) {
        if ((countsByStream.get(selectedStreamId) ?? 0) > 0) sequenceIds.add(sequenceId);
      }
    }
    return [...sequenceIds].sort();
  }, [
    dirtyFrameCountsBySequenceAndStream,
    saveConflictFrameCountsBySequenceAndStream,
    selectedStreamId,
    validationIssueFrameCountsBySequenceAndStream,
  ]);
  const sequenceDirectoryFilterGroups = useMemo<readonly FilterGroup[]>(
    () => [
      {
        value: SEQUENCE_CONTENT_FILTER_GROUP,
        label: annotationStudioText("sequenceContentFilter", locale),
        allLabel: annotationStudioText("allSequenceContent", locale),
        display: "select",
        filters: [
          {
            value: "captured",
            label: annotationStudioText("capturedSequenceContent", locale),
            description: annotationStudioText("capturedSequenceContentDescription", locale),
            ...(sequenceContentCounts ? { count: sequenceContentCounts.captured } : {}),
          },
          {
            value: "annotated",
            label: annotationStudioText("annotatedSequenceContent", locale),
            description: annotationStudioText("annotatedSequenceContentDescription", locale),
            ...(sequenceContentCounts ? { count: sequenceContentCounts.annotated } : {}),
          },
        ],
      },
      {
        value: SEQUENCE_STATUS_FILTER_GROUP,
        label: annotationStudioText("sequenceStatusFilter", locale),
        allLabel: annotationStudioText("allSequenceStatuses", locale),
        display: "select",
        filters: [
          {
            value: NEEDS_ATTENTION_FILTER,
            label: annotationStudioText("needsAttentionSequenceStatus", locale),
            description: annotationStudioText("needsAttentionSequenceStatusDescription", locale),
            count: attentionSequenceIds.length,
          },
        ],
      },
    ],
    [attentionSequenceIds.length, locale, sequenceContentCounts],
  );
  const cameraNavigation = useCompositeListNavigation({
    items: globalStreams,
    getKey: (item) => item.id,
    selectedKey: selectedStreamId,
    onSelect: (item) => onSelectStream(item.id),
    scrollContainerRef: cameraScrollerRef,
  });
  const directoryContentScope: SequenceDirectoryContentScope =
    sequenceDirectory?.query.contentScope ?? "all";
  const directoryAttentionFilterActive =
    sequenceDirectory?.query.attentionSequenceIds !== undefined;
  const directoryAttentionScopeKey =
    sequenceDirectory?.query.attentionSequenceIds?.join("\u0000") ?? null;

  useEffect(() => {
    if (!sequenceDirectory || !onSequenceDirectoryQueryChange) return;
    const expectedAttentionSequenceIds = directoryAttentionFilterActive
      ? attentionSequenceIds
      : undefined;
    if (
      sequenceDirectory.query.streamId === selectedStreamId &&
      sequenceDirectory.query.contentScope !== undefined &&
      sameSequenceIdScope(
        sequenceDirectory.query.attentionSequenceIds,
        expectedAttentionSequenceIds,
      )
    ) {
      return;
    }
    onSequenceDirectoryQueryChange(
      createSequenceDirectoryQuery(
        selectedStreamId,
        directoryContentScope,
        expectedAttentionSequenceIds,
      ),
    );
  }, [
    attentionSequenceIds,
    directoryAttentionFilterActive,
    directoryContentScope,
    onSequenceDirectoryQueryChange,
    selectedStreamId,
    sequenceDirectory,
  ]);

  const handleSequenceDirectoryFilterChange = useCallback(
    (selection: FilterSelection) => {
      if (!sequenceDirectory || !onSequenceDirectoryQueryChange) return;
      const selectedScope = selection[SEQUENCE_CONTENT_FILTER_GROUP]?.[0];
      const contentScope: SequenceDirectoryContentScope =
        selectedScope === "captured" || selectedScope === "annotated" ? selectedScope : "all";
      const attentionFilterActive =
        selection[SEQUENCE_STATUS_FILTER_GROUP]?.[0] === NEEDS_ATTENTION_FILTER;
      const nextAttentionSequenceIds = attentionFilterActive ? attentionSequenceIds : undefined;
      if (
        contentScope === directoryContentScope &&
        sequenceDirectory.query.streamId === selectedStreamId &&
        sameSequenceIdScope(sequenceDirectory.query.attentionSequenceIds, nextAttentionSequenceIds)
      ) {
        return;
      }
      onSequenceDirectoryQueryChange(
        createSequenceDirectoryQuery(selectedStreamId, contentScope, nextAttentionSequenceIds),
      );
    },
    [
      attentionSequenceIds,
      directoryContentScope,
      onSequenceDirectoryQueryChange,
      selectedStreamId,
      sequenceDirectory,
    ],
  );
  const sequenceDirectoryDefaultSelection = useMemo<FilterSelection>(() => {
    const selection: Record<string, readonly string[]> = {};
    if (directoryContentScope !== "all") {
      selection[SEQUENCE_CONTENT_FILTER_GROUP] = [directoryContentScope];
    }
    if (directoryAttentionFilterActive) {
      selection[SEQUENCE_STATUS_FILTER_GROUP] = [NEEDS_ATTENTION_FILTER];
    }
    return selection;
  }, [directoryAttentionFilterActive, directoryContentScope]);
  const [sequenceDirectoryFilterController] = useState(
    () =>
      new FilterController({
        groups: sequenceDirectoryFilterGroups,
        initialSelection: sequenceDirectoryDefaultSelection,
      }),
  );
  useEffect(() => {
    sequenceDirectoryFilterController.setGroups(sequenceDirectoryFilterGroups);
  }, [sequenceDirectoryFilterController, sequenceDirectoryFilterGroups]);
  useEffect(() => {
    sequenceDirectoryFilterController.replaceSelection(sequenceDirectoryDefaultSelection);
  }, [sequenceDirectoryDefaultSelection, sequenceDirectoryFilterController]);
  useEffect(
    () => () => sequenceDirectoryFilterController.dispose(),
    [sequenceDirectoryFilterController],
  );

  const episodeContextProps = (
    item: AnnotationSequence,
    context: Pick<ListViewItemContext, "selected" | "pinned">,
  ): EpisodeContextItemProps => {
    const streamId = selectedStreamId ?? "";
    const unannotatedFrameCount =
      unannotatedFrameCountsBySequenceAndStream.get(item.id)?.get(streamId) ?? 0;
    const annotatedFrameCount =
      annotatedFrameCountsBySequenceAndStream.get(item.id)?.get(streamId) ?? 0;
    const trackingAvailable = trackableSequenceIds.has(item.id);
    const trackingRunnable = runnableTrackingSequenceIds.has(item.id);
    const trackingTargetActive =
      trackingPreviewTargetMedia?.sequenceId === item.id &&
      trackingPreviewTargetMedia.streamId === selectedStreamId;
    const trackingState =
      trackingTargetActive &&
      (trackingPreviewStatus === "starting" || trackingPreviewStatus === "running")
        ? "running"
        : trackingTargetActive && trackingPreviewStatus === "stopped"
          ? "paused"
          : trackingTargetActive && trackingPreviewStatus === "error"
            ? "failed"
            : "idle";
    const pinned = context.pinned ?? false;

    return {
      item,
      selected: context.selected,
      newlyAdded: newSequenceIds.has(item.id),
      locale,
      selectedStreamLabel: selectedStream?.label ?? selectedStreamId,
      unannotatedFrameCount,
      annotatedFrameCount,
      dirtyFrameCount: dirtyFrameCountsBySequenceAndStream.get(item.id)?.get(streamId) ?? 0,
      saveConflictFrameCount:
        saveConflictFrameCountsBySequenceAndStream.get(item.id)?.get(streamId) ?? 0,
      validationIssueFrameCount:
        validationIssueFrameCountsBySequenceAndStream.get(item.id)?.get(streamId) ?? 0,
      trackingAvailable,
      trackingRunnable,
      trackingState,
      trackingErrorMessage: trackingState === "failed" ? trackingPreviewErrorMessage : undefined,
      sequenceActionConfigured: renderSequenceAction !== undefined,
      pinned,
      onStartTrackingPreview,
      onStopTrackingPreview,
    };
  };

  const renderEpisodeRow = (item: AnnotationSequence, context: ListViewItemContext) => ({
    content: renderEpisodeContent(item, context),
    actions: renderEpisodeActions(item, context),
  });

  const renderEpisodeContent = (
    item: AnnotationSequence,
    context: ListViewItemContext,
  ): ReactNode => <EpisodeContextContent {...episodeContextProps(item, context)} />;

  const renderEpisodeActions = (
    item: AnnotationSequence,
    context: ListViewItemContext,
  ): ReactNode => (
    <EpisodeContextActions
      {...episodeContextProps(item, context)}
      sequenceAction={renderSequenceAction?.({
        sequence: item,
        streamId: selectedStreamId,
        selected: context.selected,
        pinned: context.pinned ?? false,
      })}
    />
  );

  const directoryListIdentity = `${selectedStreamId ?? ""}:${directoryContentScope}:${directoryAttentionScopeKey ?? "all"}`;
  const directoryListStatus: ListViewProps<AnnotationSequence>["status"] =
    sequenceDirectory?.status === "error" && directorySequences.length === 0
      ? "error"
      : (sequenceDirectory?.status === "idle" || sequenceDirectory?.status === "loading") &&
          directorySequences.length === 0
        ? "loading"
        : "success";

  return (
    <Root>
      {isRenderableNode(contextLead) ? (
        <ContextLead data-studio-context-lead>{contextLead}</ContextLead>
      ) : null}
      <CameraListSection data-context-list-section="camera">
        <Text variant={defaultTextVariants["caption-bold"]}>{labels.stream[locale]}</Text>
        <CameraListViewport data-context-list-viewport="camera">
          <CameraListScroller ref={cameraScrollerRef} gutter="auto">
            {globalStreams.length === 0 ? (
              <ContextListEmpty>{annotationStudioText("noCameras", locale)}</ContextListEmpty>
            ) : (
              <ContextListSurface
                aria-label={labels.stream[locale]}
                data-studio-context-list="camera"
              >
                {globalStreams.map((item, index) => {
                  const selected = item.id === selectedStreamId;
                  const dirtyFrameCount = dirtyFrameCountsByStream.get(item.id) ?? 0;
                  const saveConflictFrameCount = saveConflictFrameCountsByStream.get(item.id) ?? 0;
                  const validationIssueFrameCount =
                    validationIssueFrameCountsByStream.get(item.id) ?? 0;
                  const episodeCount = sequenceDirectory
                    ? (sequenceDirectory.streamFacets.find((facet) => facet.streamId === item.id)
                        ?.totalCount ?? null)
                    : sequences.filter((candidate) => candidate.streamIds.includes(item.id)).length;
                  const capturedFrameCount = capturedFrameCountsByStream.get(item.id) ?? 0;
                  const unannotatedFrameCount = totalUnannotatedFramesByStream.get(item.id) ?? 0;
                  const annotatedFrameCount = totalAnnotatedFramesByStream.get(item.id) ?? 0;
                  const metricsDescriptionId = `${cameraMetricsId}-${index}`;
                  const cameraDescription = [
                    episodesLabel(episodeCount, labels, locale),
                    framesWithoutAnnotationLabel(unannotatedFrameCount, locale),
                    framesWithAnnotationLabel(annotatedFrameCount, locale),
                    dirtyFrameCount > 0 ||
                    saveConflictFrameCount > 0 ||
                    validationIssueFrameCount > 0
                      ? contextStatusLabel({
                          locale,
                          dirtyFrameCount,
                          saveConflictFrameCount,
                          validationIssueFrameCount,
                        })
                      : null,
                    trackableStreamIds.has(item.id)
                      ? annotationStudioText("trackingAvailable", locale)
                      : null,
                  ]
                    .filter(Boolean)
                    .join(". ");

                  return (
                    <CameraRow
                      key={item.id}
                      $selected={selected}
                      data-selected={selected}
                      data-studio-context-row="camera"
                    >
                      <ContextPrimaryButton
                        type="button"
                        data-studio-list-primary=""
                        data-camera-select={item.id}
                        aria-label={item.label}
                        aria-describedby={metricsDescriptionId}
                        {...cameraNavigation.getPrimaryNavigationProps(item, index)}
                        onDoubleClick={() => onOpenStreamInVideo(item.id)}
                      >
                        <CameraItemIdentity
                          data-camera-id={item.id}
                          data-camera-episode-count={episodeCount ?? undefined}
                          data-camera-captured-frame-count={capturedFrameCount}
                          data-camera-unannotated-frame-count={unannotatedFrameCount}
                          data-camera-annotated-frame-count={annotatedFrameCount}
                        >
                          <CameraTitleRow data-context-item-title="">
                            <ItemName $selected={selected} fullText={item.label}>
                              {item.label}
                            </ItemName>
                            {dirtyFrameCount > 0 ||
                            saveConflictFrameCount > 0 ||
                            validationIssueFrameCount > 0 ? (
                              <ContextStatusIndicator
                                context="camera"
                                contextId={item.id}
                                locale={locale}
                                dirtyFrameCount={dirtyFrameCount}
                                saveConflictFrameCount={saveConflictFrameCount}
                                validationIssueFrameCount={validationIssueFrameCount}
                              />
                            ) : null}
                          </CameraTitleRow>
                          <CameraMetrics aria-hidden="true" data-camera-metrics="">
                            <FrameCountStatus
                              $empty={episodeCount === 0}
                              data-camera-episode-count-indicator=""
                            >
                              <ContextMetricIconTooltip
                                content={episodesLabel(episodeCount, labels, locale)}
                                placement="right"
                                describeTrigger={false}
                                data-camera-metric-tooltip-anchor="episode"
                              >
                                <EpisodeIcon aria-hidden="true" />
                              </ContextMetricIconTooltip>
                              <span>{episodeCount ?? "—"}</span>
                            </FrameCountStatus>
                            <UnannotatedFrameCountStatus
                              $empty={unannotatedFrameCount === 0}
                              data-camera-unannotated-frame-count-indicator=""
                            >
                              <ContextMetricIconTooltip
                                content={framesWithoutAnnotationLabel(
                                  unannotatedFrameCount,
                                  locale,
                                )}
                                placement="right"
                                describeTrigger={false}
                                data-camera-metric-tooltip-anchor="unannotated"
                              >
                                <FrameIcon
                                  data-unannotated-frame-count-icon=""
                                  aria-hidden="true"
                                />
                              </ContextMetricIconTooltip>
                              <span>{unannotatedFrameCount}</span>
                            </UnannotatedFrameCountStatus>
                            <AnnotationFrameCountStatus
                              $empty={annotatedFrameCount === 0}
                              data-camera-annotated-frame-count-indicator=""
                            >
                              <ContextMetricIconTooltip
                                content={framesWithAnnotationLabel(annotatedFrameCount, locale)}
                                placement="right"
                                describeTrigger={false}
                                data-camera-metric-tooltip-anchor="annotated"
                              >
                                <AnnotationIcon
                                  data-annotation-frame-count-icon=""
                                  aria-hidden="true"
                                />
                              </ContextMetricIconTooltip>
                              <span>{annotatedFrameCount}</span>
                            </AnnotationFrameCountStatus>
                          </CameraMetrics>
                          <CameraTrackingStatusSlot data-camera-tracking-status-slot="">
                            {trackableStreamIds.has(item.id) ? (
                              <TrackingAvailabilityIndicator
                                context="camera"
                                contextId={item.id}
                                locale={locale}
                              />
                            ) : null}
                          </CameraTrackingStatusSlot>
                        </CameraItemIdentity>
                        <SrOnly id={metricsDescriptionId}>{cameraDescription}.</SrOnly>
                      </ContextPrimaryButton>
                    </CameraRow>
                  );
                })}
              </ContextListSurface>
            )}
          </CameraListScroller>
        </CameraListViewport>
      </CameraListSection>
      <ListSection data-context-list-section="episode">
        <EpisodeDirectoryHeader>
          <ListSectionHeading>
            <Text variant={defaultTextVariants["caption-bold"]}>{labels.sequence[locale]}</Text>
            {scopedNewSequenceCount > 0 ? (
              <NewEpisodeSummaryChip
                color={NEW_SEQUENCE_CHIP_COLOR}
                size="xs"
                variant="outline"
                showIcon={false}
                data-new-episode-summary=""
              >
                {annotationStudioNewSequenceSummary(scopedNewSequenceCount, locale)}
              </NewEpisodeSummaryChip>
            ) : null}
            {sequenceDirectory ? (
              <EpisodeResultCount variant={defaultTextVariants["caption-normal"]}>
                {annotationStudioSequenceDirectoryCount(
                  sequenceDirectory.resultCount,
                  sequenceDirectory.items.length,
                  sequenceDirectory.totalSequenceCount,
                  locale,
                )}
              </EpisodeResultCount>
            ) : null}
          </ListSectionHeading>
          {sequenceDirectory ? (
            <EpisodeDirectoryControls data-sequence-directory-controls="">
              <EpisodeDirectoryFilter
                controller={sequenceDirectoryFilterController}
                label={annotationStudioText("sequenceDirectoryFilters", locale)}
                onSelectionChange={handleSequenceDirectoryFilterChange}
                clearLabel={annotationStudioText("clearSequenceDirectoryFilters", locale)}
                clearIcon={<CloseIcon aria-hidden="true" />}
              />
            </EpisodeDirectoryControls>
          ) : null}
        </EpisodeDirectoryHeader>
        {sequenceDirectory ? (
          <EpisodeResultsViewport
            data-context-list-viewport="episode"
            data-studio-context-list="episode"
            data-sequence-directory-virtualized=""
            data-sequence-directory-loading={directoryListStatus === "loading" ? "" : undefined}
            data-sequence-directory-error={directoryListStatus === "error" ? "" : undefined}
            data-sequence-directory-empty={
              directoryListStatus === "success" && directorySequences.length === 0 ? "" : undefined
            }
            data-sequence-directory-loading-more={
              sequenceDirectory.isLoadingNextPage ? "" : undefined
            }
            data-tracking-start-blocked={trackingStartBlocked || undefined}
          >
            <EpisodeListView
              key={directoryListIdentity}
              $trackingStartBlocked={trackingStartBlocked}
              items={directorySequences}
              pinned={
                pinnedSequence
                  ? {
                      items: [pinnedSequence],
                      label: annotationStudioText("selectedEpisodePinnedGroup", locale),
                    }
                  : undefined
              }
              getKey={(item) => item.id}
              selectedKey={selectedSequenceId}
              onSelect={(item) => onSelectSequence(item.id)}
              onItemDoubleClick={(item) => onOpenSequenceInVideo(item.id)}
              renderItem={renderEpisodeRow}
              virtualization={{ overscan: ASYNC_EPISODE_OVERSCAN }}
              resultWindow={{ total: sequenceDirectory.resultCount }}
              status={directoryListStatus}
              errorMessage={
                sequenceDirectory.error?.[locale] ?? annotationStudioText("operationFailed", locale)
              }
              emptyMessage={annotationStudioText("noSequenceResults", locale)}
              onRetry={onRetrySequenceDirectory}
              retryLabel={annotationStudioText("retrySequenceDirectory", locale)}
              hasMore={sequenceDirectory.hasNextPage}
              loadingMore={sequenceDirectory.isLoadingNextPage}
              onLoadMore={onLoadNextSequencePage}
              ariaLabel={labels.sequence[locale]}
            />
            {sequenceDirectory.nextPageError &&
            directorySequences.length > 0 &&
            !sequenceDirectory.isLoadingNextPage ? (
              <DirectoryStatus data-sequence-directory-next-page-error="">
                <span>{sequenceDirectory.nextPageError[locale]}</span>
                {onRetrySequenceDirectory ? (
                  <DirectoryRetryButton type="button" onClick={onRetrySequenceDirectory}>
                    {annotationStudioText("retrySequenceDirectory", locale)}
                  </DirectoryRetryButton>
                ) : null}
              </DirectoryStatus>
            ) : null}
          </EpisodeResultsViewport>
        ) : (
          <ListViewport
            data-context-list-viewport="episode"
            data-studio-context-list="episode"
            data-tracking-start-blocked={trackingStartBlocked || undefined}
          >
            <EpisodeListView
              $trackingStartBlocked={trackingStartBlocked}
              items={staticScopedSequences}
              getKey={(item) => item.id}
              selectedKey={selectedSequenceId}
              onSelect={(item) => onSelectSequence(item.id)}
              onItemDoubleClick={(item) => onOpenSequenceInVideo(item.id)}
              renderItem={renderEpisodeRow}
              resultWindow={{ total: staticScopedSequences.length }}
              status="success"
              emptyMessage={annotationStudioText("emptyWorkspace", locale)}
              ariaLabel={labels.sequence[locale]}
            />
          </ListViewport>
        )}
      </ListSection>
    </Root>
  );
}

// 라이브 tracking 스트림 등 고빈도 스냅샷 패치에서 rail 서브트리 재조정을 차단한다.
export const StudioContextRail = memo(StudioContextRailComponent);
